import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Avatar from '@material-ui/core/Avatar';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import { HeadsetMic as CallCenterIcon } from '@material-ui/icons';
import { blue } from '@material-ui/core/colors';
import {
  Box,
  Checkbox,
  ListItemSecondaryAction,
  Typography,
} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import DialogActions from '@material-ui/core/DialogActions';
import useCallCenter from 'src/hooks/useCallCenter';
import { useSnackbar } from 'notistack';
import { FixedSizeList } from 'react-window';

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  checkAll: {
    position: 'absolute',
    right: theme.spacing(2),
    top: theme.spacing(1),
    color: theme.palette.secondary.main,
  },
});

const useStyles = makeStyles({
  avatar: {
    backgroundColor: blue[100],
    color: blue[600],
  },
  box: {},
  list: {
    paddingTop: 0,
  },
  text: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
});

const DialogTitle = withStyles(styles)(
  ({
    classes,
    onToggleSelectAll,
    areAllSelected,
    areAllDeselected,
    children
  }) => (
    <MuiDialogTitle className={classes.root} disableTypography>
      <Typography variant="h4">{children}</Typography>
      <Checkbox
        id="call-center-select-all-checkbox"
        className={classes.checkAll}
        edge="end"
        onChange={onToggleSelectAll}
        checked={areAllSelected}
        indeterminate={!areAllDeselected && !areAllSelected}
      />
    </MuiDialogTitle>
  ),
);

DialogTitle.propTypes = {
  classes: PropTypes.object,
  children: PropTypes.any,
  areAllSelected: PropTypes.bool,
  areAllDeselected: PropTypes.bool,
  onToggleSelectAll: PropTypes.func.isRequired,
};

DialogTitle.defaultProps = {
  onToggleSelectAll: () => {},
};

function CallCenterItem({
  name,
  index,
  id,
  style,
  isSelected,
  toggleSelected,
}) {
  const classes = useStyles();

  return (
    <ListItem
      button
      selected={isSelected}
      onClick={toggleSelected}
      ContainerProps={{ style }}
      ContainerComponent="div"
      key={index}
    >
      <ListItemAvatar>
        <Avatar className={classes.avatar}>
          <CallCenterIcon />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        title={name}
        classes={{ primary: classes.text }}
        primary={name}
      />
      <ListItemSecondaryAction>
        <Checkbox
          id={`${id}`}
          edge="end"
          onChange={toggleSelected}
          checked={isSelected}
        />
      </ListItemSecondaryAction>
    </ListItem>
  );
}

CallCenterItem.propTypes = {
  name: PropTypes.string,
  index: PropTypes.number,
  id: PropTypes.string,
  style: PropTypes.object,
  isSelected: PropTypes.bool,
  toggleSelected: PropTypes.func,
};

CallCenterItem.defaultProps = {
  toggleSelected: () => {},
  style: {},
};

function CallCenterDialog({ open, onClose }) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const {
    allCallCenters,
    toggleSelectedCallCenter,
    isCallCenterSelected,
    areAllCallCentersSelected,
    areAllCallCentersDeselected,
    toggleAllSelectedCallCenters,
    saveCallCenters,
  } = useCallCenter();

  const handleClose = () => {
    onClose();
  };

  const handleSave = async () => {
    try {
      saveCallCenters();
      onClose();
    } catch (error) {
      enqueueSnackbar(
        // 'Unable to save changes'
        error.message ?? 'Unable to save changes',
        { variant: 'error' },
      );
    }
  };

  if (!allCallCenters) return null;

  return (
    <Dialog onClose={handleClose} open={open} maxWidth="xs" fullWidth>
      <DialogTitle
        areAllSelected={areAllCallCentersSelected()}
        areAllDeselected={areAllCallCentersDeselected()}
        onToggleSelectAll={toggleAllSelectedCallCenters}
      >
        Call Centers
      </DialogTitle>
      <Box className={classes.box}>
        {
          // use a virtualized list if there are over 100 items to prevent slowness
          allCallCenters.length > 100
            ? (
              <FixedSizeList
                className={classes.list}
                height={250}
                width={444}
                itemSize={72}
                itemCount={allCallCenters.length}
              >
                {({ index, style }) => (
                  <CallCenterItem
                    id={`call-center-item-${index}`}
                    name={allCallCenters[index].callCenterName}
                    index={index}
                    style={style}
                    toggleSelected={
                        toggleSelectedCallCenter(allCallCenters[index].id)
                      }
                    isSelected={
                        isCallCenterSelected(allCallCenters[index].id)
                      }
                  />
                )}
              </FixedSizeList>
            )
            : (
              <List id="call-center-list" className={classes.list}>
                {allCallCenters.map((callCenter, index) => (
                  <CallCenterItem
                    id={`call-center-list-item-${callCenter.id}`}
                    key={callCenter.id}
                    name={callCenter.callCenterName}
                    index={index}
                    toggleSelected={toggleSelectedCallCenter(callCenter.id)}
                    isSelected={isCallCenterSelected(callCenter.id)}
                  />
                ))}
              </List>
            )
        }
      </Box>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Close
        </Button>
        <Button onClick={handleSave} color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

CallCenterDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

CallCenterDialog.defaultProps = {
  onClose: () => {},
};

export default CallCenterDialog;
